// Caja de búsqueda de /negocios sin depender del mapa. El listado, el directorio y los pines se
// piden por caja (swLat/swLng/neLat/neLng), y antes esa caja salía únicamente del mapa: en el
// teléfono la vista de lista no monta el mapa, así que nunca llegaba y los esqueletos quedaban
// para siempre. Acá se calcula, con la misma proyección de Mapbox (Web Mercator, teselas de
// 512 px), la caja que mostraría el mapa centrado en la ubicación de la URL. Cuando el mapa
// después informa la suya, solo se vuelve a pedir si es distinta de verdad (ver boundsAreClose).

const TILE_SIZE = 512;
const MAX_LAT = 85.051129;

// Zoom por defecto del mapa cuando la URL no trae `distance`.
export const DEFAULT_ZOOM = 11;

// Mismo cálculo que usa el mapa para pasar de radio en km a zoom.
function distanceToZoom(distanceKm) {
  const earthCircumferenceKm = 40075;
  const visibleFraction = (distanceKm * 2) / earthCircumferenceKm;
  const zoom = Math.log2(1 / visibleFraction);
  return Math.max(1, Math.min(20, zoom));
}

export function zoomForDistance(distance) {
  const km = parseFloat(distance);
  return Number.isFinite(km) && km > 0 ? distanceToZoom(km) : DEFAULT_ZOOM;
}

const clampLat = (lat) => Math.max(-MAX_LAT, Math.min(MAX_LAT, lat));

function project(lat, lng, worldSize) {
  const sin = Math.sin((clampLat(lat) * Math.PI) / 180);
  return {
    x: ((lng + 180) / 360) * worldSize,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * worldSize
  };
}

function unproject(x, y, worldSize) {
  const n = Math.PI * (1 - (2 * y) / worldSize);
  return {
    lat: (Math.atan(Math.sinh(n)) * 180) / Math.PI,
    lng: (x / worldSize) * 360 - 180
  };
}

/**
 * Caja visible de un mapa de `width`×`height` px centrado en lat/lng con el zoom dado.
 * @returns {{ swLat: number, swLng: number, neLat: number, neLng: number } | null}
 */
export function boundsAround({ lat, lng, zoom, width, height }) {
  const la = parseFloat(lat);
  const ln = parseFloat(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return null;
  const w = Math.max(1, width || 0);
  const h = Math.max(1, height || 0);
  const worldSize = TILE_SIZE * Math.pow(2, zoom);
  const center = project(la, ln, worldSize);
  const sw = unproject(center.x - w / 2, center.y + h / 2, worldSize);
  const ne = unproject(center.x + w / 2, center.y - h / 2, worldSize);
  return { swLat: sw.lat, swLng: sw.lng, neLat: ne.lat, neLng: ne.lng };
}

/**
 * Dos cajas son «la misma búsqueda» si ningún borde se corrió más que `tolerance` del alto/ancho.
 * Evita repetir el pedido cuando el mapa termina de cargar y reporta casi la misma caja.
 */
export function boundsAreClose(a, b, tolerance = 0.05) {
  if (!a || !b) return false;
  const latSpan = Math.abs(a.neLat - a.swLat) || 1e-9;
  const lngSpan = Math.abs(a.neLng - a.swLng) || 1e-9;
  return (
    Math.abs(a.swLat - b.swLat) / latSpan <= tolerance &&
    Math.abs(a.neLat - b.neLat) / latSpan <= tolerance &&
    Math.abs(a.swLng - b.swLng) / lngSpan <= tolerance &&
    Math.abs(a.neLng - b.neLng) / lngSpan <= tolerance
  );
}

/** Parte de la URL de la API con la caja; redondeada para que la clave de SWR sea estable. */
export function boundsQuery({ swLat, swLng, neLat, neLng }) {
  const r = (n) => Number(n).toFixed(5);
  return `swLat=${r(swLat)}&swLng=${r(swLng)}&neLat=${r(neLat)}&neLng=${r(neLng)}`;
}

// /negocios abre a escala de barrio: los negocios con fotos se ven como fotos desde el primer
// momento (de lejos son puntos). El radio de la URL se respeta, pero con un acercamiento de más.
const MAP_ZOOM_BOOST = 1.6;
const MAP_ZOOM_MAX = 15;

export function mapZoomFor(distance) {
  return Math.min(MAP_ZOOM_MAX, zoomForDistance(distance) + MAP_ZOOM_BOOST);
}
